/**
 * Every person-facing string that has a Filipino text. Spec 001, contracts/i18n.md.
 *
 * One entry per key. TypeScript rejects an entry without `en` or `fil`, and the gate
 * (scripts/check-i18n.mjs) rejects an empty one or a placeholder that one language has and the
 * other does not.
 *
 * `filStatus: 'draft'` means the Filipino was written during development and a native speaker has
 * not approved it yet. Spec 012 is that review. Do not mark a text 'approved' in code review: only
 * the reviewer does.
 *
 * Placeholders are `{name}`. A plural entry has `one` and `other`, picked by `{n}`.
 */

export type Lang = 'en' | 'fil'
export type Plural = { one: string; other: string }
export type Text = string | Plural
export type Entry = { en: Text; fil: Text; filStatus: 'draft' | 'approved' }

const d = (en: Text, fil: Text): Entry => ({ en, fil, filStatus: 'draft' })

export const catalogue = {
  // Language menu
  'lang.label': d('Language', 'Wika'),
  'lang.en': d('English', 'English'),
  'lang.fil': d('Filipino', 'Filipino'),

  // Shared
  'common.email': d('Email', 'Email'),
  'common.password': d('Password', 'Password'),
  'common.emailPlaceholder': d('you@company.com', 'ikaw@kompanya.com'),
  'common.passwordRule': d('At least 8 characters', 'Hindi bababa sa 8 character'),

  // Sign in
  'signIn.title': d('Sign in to MySerbisyo', 'Mag-sign in sa MySerbisyo'),
  'signIn.subtitle': d("Welcome back. Let's get to work.", 'Maligayang pagbabalik. Simulan na natin.'),
  'signIn.forgot': d('Forgot password?', 'Nakalimutan ang password?'),
  'signIn.button': d('Sign in', 'Mag-sign in'),
  'signIn.busy': d('Signing in…', 'Nagsa-sign in…'),
  'signIn.badLogin': d('Incorrect email or password', 'Mali ang email o password'),
  'signIn.newHere': d('New to MySerbisyo?', 'Bago sa MySerbisyo?'),
  'signIn.createAccount': d('Create a free account', 'Gumawa ng libreng account'),

  // Forgot password
  'reset.title': d('Reset your password', 'I-reset ang iyong password'),
  'reset.subtitle': d("We'll email you a link to set a new password.", 'Padadalhan ka namin ng link sa email para makagawa ng bagong password.'),
  'reset.enterEmail': d('Enter your email first', 'Ilagay muna ang iyong email'),
  'reset.sent': d('If that email has an account, a reset link is on its way.', 'Kung may account ang email na iyan, padating na ang reset link.'),
  'reset.button': d('Send reset link', 'Ipadala ang reset link'),
  'reset.busy': d('Sending…', 'Ipinapadala…'),
  'reset.back': d('Back to sign in', 'Bumalik sa sign in'),

  // Set a new password
  'newPassword.title': d('Set a new password', 'Gumawa ng bagong password'),
  'newPassword.openFromEmail': d(
    'Open this page from the reset link in your email. If you got here another way, request a new link from the sign-in page.',
    'Buksan ang page na ito mula sa reset link sa iyong email. Kung iba ang pinanggalingan mo, humingi ng bagong link sa sign-in page.',
  ),
  'newPassword.new': d('New password', 'Bagong password'),
  'newPassword.confirm': d('Confirm password', 'Kumpirmahin ang password'),
  'newPassword.confirmPlaceholder': d('Re-enter password', 'Ilagay ulit ang password'),
  'newPassword.tooShort': d('Password must be at least 8 characters.', 'Dapat hindi bababa sa 8 character ang password.'),
  'newPassword.mismatch': d("Passwords don't match.", 'Hindi magkapareho ang mga password.'),
  'newPassword.button': d('Update password', 'I-update ang password'),
  'newPassword.busy': d('Saving…', 'Sine-save…'),
  'newPassword.done': d('Password updated. Taking you to your dashboard…', 'Na-update na ang password. Dadalhin ka namin sa iyong dashboard…'),

  // Sign up (trade person)
  'signUp.title': d('Create your MySerbisyo account', 'Gumawa ng iyong MySerbisyo account'),
  'signUp.subtitle': d('Free to join. You pay only a commission on paid bookings.', 'Libreng sumali. Komisyon lang sa mga bayad na booking ang babayaran mo.'),
  'signUp.business': d('Business name', 'Pangalan ng negosyo'),
  'signUp.businessPlaceholder': d('Santos Aircon Services', 'Santos Aircon Services'),
  'signUp.firstName': d('First name', 'Pangalan'),
  'signUp.lastName': d('Last name', 'Apelyido'),
  'signUp.firstNamePlaceholder': d('Juan', 'Juan'),
  'signUp.lastNamePlaceholder': d('dela Cruz', 'dela Cruz'),
  'signUp.workEmail': d('Work email', 'Email sa trabaho'),
  'signUp.missing': d('Fill in every field. The password must be at least 8 characters.', 'Punan ang lahat ng field. Dapat hindi bababa sa 8 character ang password.'),
  'signUp.failed': d('Could not create your account.', 'Hindi nagawa ang iyong account.'),
  'signUp.button': d('Create account', 'Gumawa ng account'),
  'signUp.busy': d('Creating your account…', 'Ginagawa ang iyong account…'),
  'signUp.haveAccount': d('Already have an account?', 'May account ka na?'),
  'signUp.signIn': d('Sign in', 'Mag-sign in'),
  'signUp.agree': d('By creating an account you agree to our {terms} and {privacy}.', 'Sa paggawa ng account, sumasang-ayon ka sa aming {terms} at {privacy}.'),
  'signUp.terms': d('Terms', 'Mga Tuntunin'),
  'signUp.privacy': d('Privacy Policy', 'Patakaran sa Privacy'),

  // Account screen
  'account.languageTitle': d('Language', 'Wika'),
  'account.languageBody': d(
    'The language for the screens you see. It follows you to every device you sign in on.',
    'Ang wika ng mga screen na nakikita mo. Susunod ito sa iyo sa bawat device na pag-sign in-an mo.',
  ),

  // Sign in by mobile number (spec 002)
  'mobile.title': d('Sign in or create an account', 'Mag-sign in o gumawa ng account'),
  'mobile.subtitle': d('Use your mobile number. We will text you a 6-digit code.', 'Gamitin ang iyong mobile number. Padadalhan ka namin ng 6-digit na code.'),
  'mobile.label': d('Mobile number', 'Mobile number'),
  'mobile.placeholder': d('0917 123 4567', '0917 123 4567'),
  'mobile.notPh': d('Enter a Philippine mobile number, for example 0917 123 4567.', 'Maglagay ng Philippine mobile number, halimbawa 0917 123 4567.'),
  'mobile.send': d('Send code', 'Ipadala ang code'),
  'mobile.sending': d('Sending…', 'Ipinapadala…'),
  'mobile.sendFailed': d('We could not send the code. Try again in a minute.', 'Hindi namin naipadala ang code. Subukan ulit pagkalipas ng isang minuto.'),
  'mobile.codeTitle': d('Enter the code', 'Ilagay ang code'),
  'mobile.codeSent': d('We sent a 6-digit code to {mobile}.', 'Nagpadala kami ng 6-digit na code sa {mobile}.'),
  'mobile.codeLabel': d('6-digit code', '6-digit na code'),
  'mobile.verify': d('Continue', 'Magpatuloy'),
  'mobile.verifying': d('Checking…', 'Sinusuri…'),
  'mobile.wrongCode': d('That code is not right. Check the SMS and try again.', 'Mali ang code. Tingnan ang SMS at subukan ulit.'),
  'mobile.resendIn': d('Send a new code in {s}s', 'Magpadala ng bagong code sa loob ng {s}s'),
  'mobile.resend': d('Send a new code', 'Magpadala ng bagong code'),
  'mobile.changeNumber': d('Use a different number', 'Gumamit ng ibang number'),
  'mobile.useEmail': d('Business owner? Sign in with email', 'May negosyo? Mag-sign in gamit ang email'),
  'mobile.useMobile': d('Sign in with a mobile number', 'Mag-sign in gamit ang mobile number'),
  'mobile.google': d('Continue with Google', 'Magpatuloy gamit ang Google'),
  'mobile.or': d('or', 'o'),
  'mobile.terms': d('By continuing you agree to our {terms} and {privacy}.', 'Sa pagpapatuloy, sumasang-ayon ka sa aming {terms} at {privacy}.'),

  // Name step
  'welcome.title': d('What is your name?', 'Ano ang pangalan mo?'),
  'welcome.subtitle': d('Trade persons see this name when you book them.', 'Makikita ng mga trade person ang pangalang ito kapag nag-book ka.'),
  'welcome.label': d('Your name', 'Pangalan mo'),
  'welcome.placeholder': d('Maria Santos', 'Maria Santos'),
  'welcome.required': d('Enter your name.', 'Ilagay ang pangalan mo.'),
  'welcome.continue': d('Continue', 'Magpatuloy'),

  // Client shell
  'client.nav.home': d('Home', 'Home'),
  'client.nav.profile': d('Profile', 'Profile'),
  'client.mode.business': d('My business', 'Aking negosyo'),
  'client.mode.offer': d('Offer your services', 'Mag-alok ng serbisyo'),
  'client.mode.hire': d('Hire a trade person', 'Kumuha ng trade person'),
  'client.signOut': d('Sign out', 'Mag-sign out'),

  // Client home
  'home.greeting': d('Hi {name}, what do you need done?', 'Hi {name}, ano ang kailangan mong ipagawa?'),
  'home.categories': d('Popular services', 'Mga sikat na serbisyo'),
  'cat.aircon': d('Aircon cleaning', 'Paglilinis ng aircon'),
  'cat.plumbing': d('Plumbing', 'Tubero'),
  'cat.electrical': d('Electrical', 'Elektrikal'),
  'cat.pest': d('Pest control', 'Pest control'),
  'cat.cleaning': d('House cleaning', 'Paglilinis ng bahay'),
  'cat.carpentry': d('Carpentry', 'Karpintero'),
  'cat.appliance': d('Appliance repair', 'Pag-aayos ng appliance'),
  'cat.moving': d('Moving', 'Paglilipat'),

  // Profile
  'profile.title': d('Your profile', 'Ang iyong profile'),
  'profile.name': d('Name', 'Pangalan'),
  'profile.mobile': d('Mobile number', 'Mobile number'),
  'profile.mobileNone': d('No mobile number yet. You need one before your first booking.', 'Wala pang mobile number. Kailangan mo ito bago ang unang booking.'),
  'profile.save': d('Save', 'I-save'),
  'profile.saving': d('Saving…', 'Sine-save…'),
  'profile.saved': d('Saved', 'Na-save'),
  'profile.business': d('Your business', 'Ang iyong negosyo'),
  'profile.businessBody': d('Offer your services on MySerbisyo and run your jobs, quotes and invoices in one place.', 'Mag-alok ng serbisyo sa MySerbisyo at pamahalaan ang iyong mga job, quote at invoice sa iisang lugar.'),
  'profile.openBusiness': d('Open my business', 'Buksan ang aking negosyo'),

  // Become a trade person
  'offer.title': d('Offer your services', 'Mag-alok ng serbisyo'),
  'offer.subtitle': d('Give your business a name. You can change it later.', 'Bigyan ng pangalan ang iyong negosyo. Mababago mo ito mamaya.'),
  'offer.label': d('Business name', 'Pangalan ng negosyo'),
  'offer.placeholder': d('Santos Aircon Services', 'Santos Aircon Services'),
  'offer.points': d('Free to join. You pay a commission only on paid bookings.', 'Libreng sumali. Komisyon lang sa mga bayad na booking ang babayaran mo.'),
  'offer.idNote': d('Before your first paid booking, we will ask for a government ID and a selfie.', 'Bago ang iyong unang bayad na booking, hihingan ka namin ng government ID at selfie.'),
  'offer.create': d('Start my business', 'Simulan ang aking negosyo'),
  'offer.creating': d('Setting up…', 'Inihahanda…'),
  'offer.already': d('This account already belongs to a business.', 'Kabilang na sa isang negosyo ang account na ito.'),
  'offer.failed': d('We could not set up your business. Try again.', 'Hindi namin naihanda ang iyong negosyo. Subukan ulit.'),

  // Public profile (spec 003)
  'pub.notFound': d('This profile is not available.', 'Hindi available ang profile na ito.'),
  'pub.notFoundBody': d('The trade person may have taken it down. Search for another trade person near you.', 'Maaaring inalis ito ng trade person. Maghanap ng ibang trade person na malapit sa iyo.'),
  'pub.findOthers': d('Find a trade person', 'Maghanap ng trade person'),
  'pub.years': d({ one: '{n} year of experience', other: '{n} years of experience' }, { one: '{n} taon ng karanasan', other: '{n} taon ng karanasan' }),
  'pub.memberSince': d('On MySerbisyo since {date}', 'Nasa MySerbisyo mula {date}'),
  'pub.about': d('About', 'Tungkol'),
  'pub.services': d('Services', 'Mga serbisyo'),
  'pub.askQuote': d('Ask for a quote', 'Humingi ng quote'),
  'pub.duration': d('About {min} min', 'Mga {min} minuto'),
  'pub.serves': d('Serves', 'Nagseserbisyo sa'),
  'pub.base': d('Based in {place}', 'Nakabase sa {place}'),
  'pub.radius': d('and within {km} km', 'at hanggang {km} km ang layo'),
  'pub.book': d('Book', 'Mag-book'),
  'pub.bookSoon': d('Booking opens soon', 'Malapit nang magbukas ang booking'),
  'pub.privacy': d('Contact details are shared after you book and pay through MySerbisyo.', 'Ibabahagi ang contact details pagkatapos mong mag-book at magbayad sa MySerbisyo.'),

  // Search (spec 004)
  'search.title': d('Find a trade person', 'Maghanap ng trade person'),
  'search.where': d('Where do you need the work?', 'Saan mo kailangan ang serbisyo?'),
  'search.wherePlaceholder': d('Barangay and city, e.g. San Antonio Pasig', 'Barangay at lungsod, hal. San Antonio Pasig'),
  'search.near': d('Use my location', 'Gamitin ang aking lokasyon'),
  'search.nearOn': d('Near your location', 'Malapit sa iyong lokasyon'),
  'search.nearFailed': d('We could not get your location. Pick a place instead.', 'Hindi namin nakuha ang iyong lokasyon. Pumili na lang ng lugar.'),
  'search.allCategories': d('All services', 'Lahat ng serbisyo'),
  'search.pickPlace': d('Pick a barangay, city or province to see who serves it.', 'Pumili ng barangay, lungsod o probinsya para makita kung sino ang nagseserbisyo dito.'),
  'search.count': d({ one: '{n} trade person serves {place}', other: '{n} trade persons serve {place}' }, { one: '{n} trade person ang nagseserbisyo sa {place}', other: '{n} trade person ang nagseserbisyo sa {place}' }),
  'search.none': d('No trade person serves {place} yet.', 'Wala pang trade person na nagseserbisyo sa {place}.'),
  'search.noneHint': d('Try the city or the province instead.', 'Subukan ang lungsod o ang probinsya.'),
  'search.from': d('From {price}', 'Mula {price}'),
  'search.quote': d('Quote on request', 'Quote kapag hiniling'),
  'search.away': d('{km} km away', '{km} km ang layo'),
  'search.basedIn': d('Based in {place}', 'Nakabase sa {place}'),
  'search.change': d('Change', 'Palitan'),
  'search.level.region': d('Region', 'Rehiyon'),
  'search.level.province': d('Province', 'Probinsya'),
  'search.level.city': d('City', 'Lungsod'),
  'search.level.barangay': d('Barangay', 'Barangay'),
  'search.signIn': d('Sign in', 'Mag-sign in'),

  // Tasks (spec 005)
  'tasks.nav': d('My tasks', 'Aking mga task'),
  'tasks.post': d('Post a task', 'Mag-post ng task'),
  'tasks.postHint': d('Describe the job and get offers from trade persons near you.', 'Ilarawan ang trabaho at makatanggap ng alok mula sa mga trade person na malapit sa iyo.'),
  'tasks.empty': d('You have not posted a task yet.', 'Wala ka pang na-post na task.'),
  'tasks.status.open': d('Open', 'Bukas'),
  'tasks.status.assigned': d('Assigned', 'May napili na'),
  'tasks.status.cancelled': d('Cancelled', 'Kinansela'),
  'tasks.status.expired': d('Expired', 'Nag-expire'),
  'tasks.offers': d({ one: '{n} offer', other: '{n} offers' }, { one: '{n} alok', other: '{n} alok' }),
  'tasks.category': d('What do you need?', 'Ano ang kailangan mo?'),
  'tasks.title': d('Title', 'Pamagat'),
  'tasks.titlePlaceholder': d('Kitchen sink leaking under the cabinet', 'Tumutulo ang lababo sa ilalim ng cabinet'),
  'tasks.description': d('Details', 'Mga detalye'),
  'tasks.descriptionPlaceholder': d('What is wrong, since when, and anything the trade person should bring.', 'Ano ang problema, kailan nagsimula, at anumang dapat dalhin ng trade person.'),
  'tasks.place': d('Where', 'Saan'),
  'tasks.placePlaceholder': d('Your barangay and city', 'Iyong barangay at lungsod'),
  'tasks.placeLevel': d('Pick a barangay or a city, not a province.', 'Pumili ng barangay o lungsod, hindi probinsya.'),
  'tasks.budget': d('Budget in pesos (optional)', 'Budget sa piso (opsyonal)'),
  'tasks.date': d('Preferred date (optional)', 'Gustong petsa (opsyonal)'),
  'tasks.photos': d('Photos (up to 5)', 'Mga larawan (hanggang 5)'),
  'tasks.addPhotos': d('Add photos', 'Magdagdag ng larawan'),
  'tasks.photoTooMany': d('Up to 5 photos.', 'Hanggang 5 larawan lang.'),
  'tasks.photoTooBig': d('Each photo must be under 10 MB.', 'Dapat mas mababa sa 10 MB ang bawat larawan.'),
  'tasks.photoType': d('Photos must be JPG, PNG, WebP or HEIC.', 'Dapat JPG, PNG, WebP o HEIC ang mga larawan.'),
  'tasks.privacy': d('Do not write your number or email. Trade persons see your first name and barangay only, until you book and pay.', 'Huwag isulat ang iyong number o email. Pangalan at barangay mo lang ang makikita ng mga trade person hanggang mag-book at magbayad ka.'),
  'tasks.submit': d('Post task', 'I-post ang task'),
  'tasks.posting': d('Posting…', 'Ipinopost…'),
  'tasks.missing': d('Choose what you need, write a title, and pick a place.', 'Piliin ang kailangan mo, sumulat ng pamagat, at pumili ng lugar.'),
  'tasks.failed': d('We could not post the task. Try again.', 'Hindi na-post ang task. Subukan ulit.'),
  'tasks.budgetLabel': d('Budget {price}', 'Budget {price}'),
  'tasks.on': d('On {date}', 'Sa {date}'),
  'tasks.expires': d('Open until {date}', 'Bukas hanggang {date}'),
  'tasks.noOffers': d('No offers yet. Trade persons who serve your area have been told.', 'Wala pang alok. Nasabihan na ang mga trade person na nagseserbisyo sa iyong lugar.'),
  'tasks.accept': d('Accept offer', 'Tanggapin ang alok'),
  'tasks.accepting': d('Accepting…', 'Tinatanggap…'),
  'tasks.accepted': d('Accepted', 'Tinanggap'),
  'tasks.declined': d('Not chosen', 'Hindi napili'),
  'tasks.viewProfile': d('View profile', 'Tingnan ang profile'),
  'tasks.cancel': d('Cancel task', 'Kanselahin ang task'),
  'tasks.keep': d('Keep it', 'Huwag na'),
  'tasks.cancelConfirm': d('Cancel this task? Every offer will be declined.', 'Kanselahin ang task na ito? Tatanggihan ang lahat ng alok.'),
  'tasks.next': d('Next: booking and payment. Coming soon.', 'Susunod: booking at bayad. Malapit na.'),
  'tasks.withdrawn': d('This offer was withdrawn.', 'Binawi ang alok na ito.'),
  'tasks.closed': d('This task is no longer open.', 'Hindi na bukas ang task na ito.'),
  'tasks.notFound': d('Task not found.', 'Hindi nakita ang task.'),
  'tasks.back': d('My tasks', 'Aking mga task'),

  // Messages (spec 006)
  'msg.nav': d('Messages', 'Mga mensahe'),
  'msg.empty': d('No messages yet.', 'Wala pang mensahe.'),
  'msg.inboxEmpty': d('No conversations yet. Message a trade person from their profile or an offer.', 'Wala pang usapan. Mag-message sa trade person mula sa kanilang profile o alok.'),
  'msg.placeholder': d('Write a message', 'Sumulat ng mensahe'),
  'msg.send': d('Send', 'Ipadala'),
  'msg.masked': d('Numbers, emails and links are hidden until the booking is paid. This keeps both of you protected.', 'Nakatago ang mga number, email at link hanggang mabayaran ang booking. Para protektado kayong dalawa.'),
  'msg.maskWarning': d('This looks like contact details. They will be hidden until the booking is paid.', 'Mukhang contact details ito. Itatago ito hanggang mabayaran ang booking.'),
  'msg.photoProblem': d('The photo could not be sent. Use a JPG or PNG under 10 MB.', 'Hindi naipadala ang larawan. Gumamit ng JPG o PNG na mas mababa sa 10 MB.'),
  'msg.about': d('About: {task}', 'Tungkol sa: {task}'),
  'msg.message': d('Message', 'Mag-message'),
  'msg.ask': d('Ask a question', 'Magtanong'),
  'msg.notFound': d('Conversation not found.', 'Hindi nakita ang usapan.'),

  // Bookings (spec 007)
  'bk.nav': d('Bookings', 'Mga booking'),
  'bk.title': d('Book {service}', 'I-book ang {service}'),
  'bk.with': d('with {business}', 'kay {business}'),
  'bk.date': d('Date', 'Petsa'),
  'bk.time': d('Time', 'Oras'),
  'bk.where': d('Barangay', 'Barangay'),
  'bk.address': d('Street address', 'Address sa kalye'),
  'bk.addressPlaceholder': d('Unit, building, street', 'Unit, gusali, kalye'),
  'bk.addressHint': d('Shared with the trade person only after you pay.', 'Ibabahagi lang sa trade person pagkatapos mong magbayad.'),
  'bk.note': d('Note for the trade person (optional)', 'Paalala sa trade person (opsyonal)'),
  'bk.price': d('Price', 'Presyo'),
  'bk.request': d('Request booking', 'Mag-request ng booking'),
  'bk.requesting': d('Sending…', 'Ipinapadala…'),
  'bk.requestHint': d('The trade person accepts or declines within 24 hours. You pay after they accept.', 'Tatanggapin o tatanggihan ito ng trade person sa loob ng 24 oras. Magbabayad ka pagkatanggap nila.'),
  'bk.missing': d('Pick a date, a time and your barangay, and write your street address.', 'Pumili ng petsa, oras at barangay, at isulat ang address sa kalye.'),
  'bk.tooSoon': d('Pick a time at least 2 hours from now.', 'Pumili ng oras na hindi bababa sa 2 oras mula ngayon.'),
  'bk.failed': d('The booking could not be made. Try again.', 'Hindi nagawa ang booking. Subukan ulit.'),
  'bk.confirmTitle': d('Confirm the booking', 'Kumpirmahin ang booking'),
  'bk.confirmHint': d('Tell {business} when to come and where. The price is their offer.', 'Sabihin kay {business} kung kailan at saan pupunta. Ang presyo ay ang kanilang alok.'),
  'bk.confirm': d('Confirm booking', 'Kumpirmahin ang booking'),
  'bk.status.requested': d('Waiting for the trade person', 'Hinihintay ang trade person'),
  'bk.status.awaiting_payment': d('Waiting for payment', 'Hinihintay ang bayad'),
  'bk.status.paid': d('Paid, held safely', 'Bayad na, ligtas na nakatabi'),
  'bk.status.done': d('Job done, please confirm', 'Tapos na, pakikumpirma'),
  'bk.status.released': d('Completed', 'Kumpleto na'),
  'bk.status.disputed': d('In dispute', 'May reklamo'),
  'bk.status.refunded': d('Refunded', 'Na-refund'),
  'bk.status.cancelled': d('Cancelled', 'Kinansela'),
  'bk.status.expired': d('Expired', 'Nag-expire'),
  'bk.empty': d('No bookings yet.', 'Wala pang booking.'),
  'bk.pay': d('Pay now', 'Magbayad ngayon'),
  'bk.paySoon': d('Payment opens soon.', 'Malapit nang magbukas ang pagbabayad.'),
  'bk.cancel': d('Cancel booking', 'Kanselahin ang booking'),
  'bk.cancelConfirm': d('Cancel this booking? Nothing has been paid.', 'Kanselahin ang booking na ito? Wala pang nababayaran.'),
  'bk.cancelledBy': d('Cancelled by {who}', 'Kinansela ni {who}'),
  'bk.byClient': d('you', 'ikaw'),
  'bk.byBusiness': d('the trade person', 'ang trade person'),
  'bk.bySystem': d('MySerbisyo', 'MySerbisyo'),
  'bk.when': d('{date} at {time}', '{date}, {time}'),

  // Escrow (spec 008)
  'pay.title': d('How do you want to pay?', 'Paano ka magbabayad?'),
  'pay.held': d('Your money is held safely by our payment partner. {business} gets it only after you confirm the job is done.', 'Ligtas na itatabi ng aming payment partner ang iyong pera. Matatanggap lang ito ni {business} pagkatapos mong kumpirmahin na tapos na ang trabaho.'),
  'pay.qrph': d('QR Ph', 'QR Ph'),
  'pay.qrphNote': d('Scan with GCash, Maya or almost any bank app. Recommended.', 'I-scan gamit ang GCash, Maya o halos anumang bank app. Inirerekomenda.'),
  'pay.gcash': d('GCash', 'GCash'),
  'pay.paymaya': d('Maya', 'Maya'),
  'pay.card': d('Credit or debit card', 'Credit o debit card'),
  'pay.continue': d('Continue to pay {price}', 'Magpatuloy sa pagbayad ng {price}'),
  'pay.opening': d('Opening the payment…', 'Binubuksan ang pagbabayad…'),
  'pay.failed': d('The payment could not be started. Try again.', 'Hindi nasimulan ang pagbabayad. Subukan ulit.'),
  'pay.paidHeld': d('Paid. Your money is held safely until you confirm the job is done.', 'Bayad na. Ligtas na nakatabi ang iyong pera hanggang kumpirmahin mong tapos na ang trabaho.'),
  'pay.checking': d('Checking your payment…', 'Sinusuri ang iyong bayad…'),
  'pay.doneTitle': d('{business} marked the job done', 'Minarkahan ni {business} na tapos na ang trabaho'),
  'pay.doneBody': d('Confirm it, or report a problem. If you do nothing, we release the payment on {date}.', 'Kumpirmahin ito, o mag-report ng problema. Kung wala kang gagawin, ire-release namin ang bayad sa {date}.'),
  'pay.confirm': d('Confirm the job is done', 'Kumpirmahing tapos na ang trabaho'),
  'pay.confirmBody': d('We will release the payment to {business}. This cannot be undone.', 'Ire-release namin ang bayad kay {business}. Hindi na ito mababawi.'),
  'pay.released': d('Completed. The payment was released to {business}.', 'Kumpleto na. Na-release na ang bayad kay {business}.'),
  'pay.refunded': d('Refunded {price}.', 'Na-refund ang {price}.'),

  // Reviews and reports (spec 011)
  'review.section': d('Reviews', 'Mga review'),
  'review.count': d({ one: '{n} review', other: '{n} reviews' }, { one: '{n} review', other: '{n} review' }),
  'review.none': d('No reviews yet.', 'Wala pang review.'),
  'review.outOf': d('{n} out of 5', '{n} sa 5'),
  'review.title': d('How was {business}?', 'Kumusta ang serbisyo ng {business}?'),
  'review.overall': d('Overall', 'Kabuuan'),
  'review.onTime': d('On time', 'Nasa oras'),
  'review.quality': d('Quality of work', 'Kalidad ng trabaho'),
  'review.value': d('Value for money', 'Sulit sa bayad'),
  'review.comment': d('Tell others about the work (optional)', 'Ikuwento sa iba ang trabaho (opsyonal)'),
  'review.photos': d('Add up to 3 photos (optional)', 'Magdagdag ng hanggang 3 litrato (opsyonal)'),
  'review.post': d('Post review', 'I-post ang review'),
  'review.posting': d('Posting', 'Pino-post'),
  'review.until': d('You can review this booking until {date}.', 'Puwede mong i-review ang booking na ito hanggang {date}.'),
  'review.yours': d('Your review', 'Ang review mo'),
  'review.removed': d('Removed by MySerbisyo: {reason}', 'Inalis ng MySerbisyo: {reason}'),
  'review.failed': d('The review could not be posted. Try again.', 'Hindi na-post ang review. Subukan ulit.'),
  'review.reply': d('Reply from {business}', 'Sagot ng {business}'),
  'review.needStars': d('Choose the stars for each line.', 'Pumili ng bituin sa bawat linya.'),
  'report.link': d('Report', 'I-report'),
  'report.profile': d('Report this profile', 'I-report ang profile na ito'),
  'report.title': d('What is wrong?', 'Ano ang mali?'),
  'report.reason.scam': d('Scam or fraud', 'Scam o panloloko'),
  'report.reason.abuse': d('Abusive or offensive', 'Mapang-abuso o nakakasakit'),
  'report.reason.fake': d('Fake or misleading', 'Peke o nanlilinlang'),
  'report.reason.personal_data': d('Shares personal data', 'Naglalabas ng personal na impormasyon'),
  'report.reason.wrong_person': d('About the wrong person', 'Maling tao ang tinutukoy'),
  'report.reason.spam': d('Spam', 'Spam'),
  'report.reason.other': d('Something else', 'Iba pa'),
  'report.details': d('Tell us more (optional)', 'Magkuwento pa (opsyonal)'),
  'report.send': d('Send report', 'Ipadala ang report'),
  'report.sent': d('Thank you. A MySerbisyo admin will look at it.', 'Salamat. Titingnan ito ng admin ng MySerbisyo.'),
  'report.already': d('You already reported this.', 'Nai-report mo na ito.'),
  'report.failed': d('The report could not be sent. Try again.', 'Hindi naipadala ang report. Subukan ulit.'),

  // Verification badges (spec 010)
  'badge.mobile': d('Mobile verified', 'Beripikadong mobile'),
  'badge.id': d('ID verified', 'Beripikadong ID'),
  'badge.registered': d('Registered business', 'Rehistradong negosyo'),
  'badge.licensed': d('Licensed until {date}', 'May lisensya hanggang {date}'),
  'badge.mobileHelp': d('Signed up with an SMS code sent to a Philippine mobile number.', 'Nag-sign up gamit ang SMS code na ipinadala sa mobile number sa Pilipinas.'),
  'badge.idHelp': d('MySerbisyo checked a government ID and a selfie of the owner.', 'Sinuri ng MySerbisyo ang government ID at selfie ng may-ari.'),
  'badge.registeredHelp': d('MySerbisyo checked the DTI or SEC registration.', 'Sinuri ng MySerbisyo ang rehistro sa DTI o SEC.'),
  'badge.licensedHelp': d('{trade} licence from {body}, checked by MySerbisyo.', 'Lisensya bilang {trade} mula sa {body}, sinuri ng MySerbisyo.'),
  'badge.title': d('Checked by MySerbisyo', 'Sinuri ng MySerbisyo'),
  'pay.notVerified': d('This trade person is not verified yet, so this booking cannot be paid. Cancel it and choose another trade person.', 'Hindi pa beripikado ang trade person na ito, kaya hindi pa mababayaran ang booking. I-cancel ito at pumili ng ibang trade person.'),

  // Disputes (spec 009)
  'dispute.report': d('Report a problem', 'Mag-report ng problema'),
  'dispute.title': d('What went wrong?', 'Ano ang naging problema?'),
  'dispute.reason.no_show': d('Nobody came', 'Walang dumating'),
  'dispute.reason.not_finished': d('Work not finished', 'Hindi tapos ang trabaho'),
  'dispute.reason.poor_work': d('Poor work', 'Hindi maayos ang trabaho'),
  'dispute.reason.damage': d('Something was damaged', 'May nasira'),
  'dispute.reason.overcharge': d('Asked to pay more than agreed', 'Siningil nang higit sa usapan'),
  'dispute.reason.other': d('Something else', 'Iba pa'),
  'dispute.describe': d('Tell us what happened', 'Ikuwento kung ano ang nangyari'),
  'dispute.describeHint': d('Write at least 10 characters. Do not write phone numbers here.', 'Sumulat ng hindi bababa sa 10 letra. Huwag maglagay ng numero ng telepono.'),
  'dispute.holdNote': d('The payment stays held until a MySerbisyo admin decides.', 'Mananatiling hawak ang bayad hanggang magpasya ang admin ng MySerbisyo.'),
  'dispute.send': d('Send report', 'Ipadala ang report'),
  'dispute.failed': d('The report could not be sent. Try again.', 'Hindi naipadala ang report. Subukan ulit.'),
  'dispute.openYou': d('You reported a problem. A MySerbisyo admin will decide by {date}. The payment stays held until then.', 'Nag-report ka ng problema. Magpapasya ang admin ng MySerbisyo bago mag-{date}. Hawak pa rin ang bayad hanggang doon.'),
  'dispute.openThem': d('{business} reported a problem. A MySerbisyo admin will decide by {date}. The payment stays held until then.', 'Nag-report ng problema ang {business}. Magpapasya ang admin ng MySerbisyo bago mag-{date}. Hawak pa rin ang bayad hanggang doon.'),
  'dispute.stillMessage': d('You can still message each other.', 'Puwede pa rin kayong mag-message sa isa\'t isa.'),
  'dispute.decided.release': d('Decided: the payment was released to {business}.', 'Napagpasyahan: na-release ang bayad kay {business}.'),
  'dispute.decided.refund': d('Decided: you were refunded {price}.', 'Napagpasyahan: na-refund sa iyo ang {price}.'),
  'dispute.decided.split': d('Decided: you were refunded {price}. The rest was released to {business}.', 'Napagpasyahan: na-refund sa iyo ang {price}. Na-release kay {business} ang natitira.'),
  'dispute.note': d('Admin note: {note}', 'Paalala ng admin: {note}'),
  'pay.cancelPaid': d('Cancel and get a refund', 'Kanselahin at mag-refund'),
  'pay.refundQuote': d('You will get {price} back, the price less the payment fee.', 'Matatanggap mo pabalik ang {price}, ang presyo bawas ang payment fee.'),
  'pay.refundTooLate': d('The job is less than 24 hours away. Message the trade person, or report a problem.', 'Wala nang 24 oras bago ang trabaho. Mag-message sa trade person, o mag-report ng problema.'),
  'pay.refundFailed': d('The refund could not be made. Try again or contact us.', 'Hindi nagawa ang refund. Subukan ulit o makipag-ugnayan sa amin.'),
  'pay.testTitle': d('Test payment', 'Test na bayad'),
  'pay.testBody': d('This is the test provider. No money moves.', 'Ito ang test provider. Walang perang gagalaw.'),
  'pay.testPay': d('Pay {price} (test)', 'Magbayad ng {price} (test)'),

  // App shell
  'app.newVersion': d('A new version of MySerbisyo is ready.', 'May bagong bersyon ng MySerbisyo.'),
  'app.newVersionBody': d('Reload when you are ready. Anything unsaved stays as it is until you do.', 'Mag-reload kapag handa ka na. Hindi mawawala ang hindi pa naka-save hangga\'t hindi ka nagre-reload.'),
  'app.reload': d('Reload', 'I-reload'),
} satisfies Record<string, Entry>

export type MessageKey = keyof typeof catalogue
